import * as dateFns from 'date-fns';
import { enUS, nb } from 'date-fns/locale';
import i18n from '../../i18n';
import {
    DONORS_HEADINGS
  } from '../../constants/donors';
import { getDonorBloodType,
  getDonorStatus,
} from '../../services/helpers';

const checkForValidDateValue = (time, mask = "d MMMM yyyy", options = {}) => {
  try {
    if (time) {
      return dateFns.format(time, mask, options)
    }
    else {
      return '-'
    }
  } catch (error) {
    console.log(error)
    return '-'
  }
}

export const getEffectiveQuarantineDate = (user) => {
  if (!user) return null;

  const eligibility = user.bookingEligibility;
  let minEligibleTs = null;
  if (eligibility && typeof eligibility === 'object') {
    Object.values(eligibility).forEach((entry) => {
      if (entry && entry.isEligible === true && entry.earliestDate) {
        const ts = new Date(entry.earliestDate).getTime();
        if (!Number.isNaN(ts) && (minEligibleTs === null || ts < minEligibleTs)) {
          minEligibleTs = ts;
        }
      }
    });
  }

  const quarantineTs = user.quarantine ? new Date(user.quarantine).getTime() : null;
  const hasQuarantine = quarantineTs !== null && !Number.isNaN(quarantineTs);

  let effectiveTs;
  if (minEligibleTs === null) {
    effectiveTs = hasQuarantine ? quarantineTs : null;
  } else if (hasQuarantine && quarantineTs >= minEligibleTs) {
    effectiveTs = quarantineTs;
  } else {
    effectiveTs = minEligibleTs;
  }

  if (effectiveTs === null) return null;
  if (effectiveTs <= Date.now()) return null;
  return effectiveTs;
};

export const prepareRowData = (currentUser) => {
    const locale = (i18n.language === 'no') ? nb : enUS;
    const isStartDateExist = currentUser?.nextAppointment?.startDate;
    const isEndDateExist = currentUser?.nextAppointment?.endDate;
    if (currentUser) return {
        [DONORS_HEADINGS.ID_NUMBER]: currentUser.id || '-',
        [DONORS_HEADINGS.LAST_DRAW]: currentUser.lastDonation ? 
        checkForValidDateValue(new Date(currentUser.lastDonation), 'd MMMM yyyy', {locale}) : 
            '-',
        [DONORS_HEADINGS.BLOOD_TYPE]: getDonorBloodType(currentUser),
        [DONORS_HEADINGS.TITER]: currentUser.titer ? i18n.t(`configuration.settings.titer.${currentUser.titer}`) : '-',
        [DONORS_HEADINGS.DONOR_STATUS]: getDonorStatus(currentUser),
        [DONORS_HEADINGS.DONOR_TYPE]: currentUser.type ? i18n.t(`common.donorType.${currentUser.type}`) : '-',
        [DONORS_HEADINGS.QUARANTINE_DUE_DATE]: (() => {
          const effective = getEffectiveQuarantineDate(currentUser);
          return effective ? checkForValidDateValue(new Date(effective), 'd MMMM yyyy', {locale}) : '-';
        })(),
        [DONORS_HEADINGS.APP_USER]: currentUser.fcmDeviceId ? i18n.t('common.appUserStatus.yes') : i18n.t('common.appUserStatus.no'),
        [DONORS_HEADINGS.SMS]: currentUser.sms === true ? i18n.t('common.smsStatus.true') : currentUser.sms === false ? i18n.t('common.smsStatus.false') : '-',
        [DONORS_HEADINGS.NEXT_APPOINTMENT]: currentUser.nextAppointment &&
        (currentUser.status !== 'new_registration') &&
        currentUser.nextAppointment !== '' && isStartDateExist && isEndDateExist ?
        `${checkForValidDateValue(new Date(currentUser.nextAppointment.startDate), 'd MMMM yyyy', { locale })}\n${checkForValidDateValue(new Date(currentUser.nextAppointment.startDate), 'HH:mm')}-${checkForValidDateValue(new Date(currentUser.nextAppointment.endDate), 'HH:mm')}`
        : '-'
    }
};

export const prepareRowDataForSorting = (currentUser) => {
  if (currentUser) return {
      [DONORS_HEADINGS.ID_NUMBER]: currentUser.id || '-',
      [DONORS_HEADINGS.LAST_DRAW]: currentUser.lastDonation || 0,
      [DONORS_HEADINGS.BLOOD_TYPE]: getDonorBloodType(currentUser),
      [DONORS_HEADINGS.TITER]: currentUser.titer ? i18n.t(`configuration.settings.titer.${currentUser.titer}`) : '-',
      [DONORS_HEADINGS.DONOR_STATUS]: getDonorStatus(currentUser),
      [DONORS_HEADINGS.DONOR_TYPE]: currentUser.type ? i18n.t(`common.donorType.${currentUser.type}`) : '-',
      [DONORS_HEADINGS.QUARANTINE_DUE_DATE]: getEffectiveQuarantineDate(currentUser) || 0,
      [DONORS_HEADINGS.APP_USER]: currentUser.fcmDeviceId ? i18n.t('common.appUserStatus.yes') : i18n.t('common.appUserStatus.no'),
      [DONORS_HEADINGS.SMS]: currentUser.sms === true ? i18n.t('common.smsStatus.true') : currentUser.sms === false ? i18n.t('common.smsStatus.false') : '-',
      [DONORS_HEADINGS.NEXT_APPOINTMENT]: currentUser.nextAppointment && 
                                            (currentUser.status !== 'new_registration') && 
                                              currentUser.nextAppointment !== '' ? currentUser.nextAppointment.startDate : 0,
  }
};

function sortDates(a, b) {
  const dateA = a ? new Date(a).getTime() : 0;
  const dateB = b ? new Date(b).getTime() : 0;

  if (dateA === dateB) return 0;
  return dateA < dateB ? -1 : 1;
}

export function sortTableData(data, sortKey, clickCount) {
  const sortedData = [...data];
  const sortOrder = clickCount % 2;

  sortedData.sort((a, b) => {
    let valA = a.sortable[sortKey];
    let valB = b.sortable[sortKey];

    if (valA === 0 || valA === "-") {
      return 1;
    }
    if (valB === 0 || valB === "-") {
      return -1;
    }

    if (sortKey === DONORS_HEADINGS.QUARANTINE_DUE_DATE || sortKey === DONORS_HEADINGS.NEXT_APPOINTMENT) {
      const result = sortDates(valA, valB);
      return sortOrder === 1 ? result : -result;
    }

    if (typeof valA === "string") {
      valA = valA.toLowerCase();
    }

    if (typeof valB === "string") {
      valB = valB.toLowerCase();
    }

    if (valA > valB) {
      return sortOrder === 1 ? -1 : 1;
    }

    if (valA < valB) {
      return sortOrder === 1 ? 1 : -1;
    }

    return 0;
  });

  return sortedData;
}
